import { FC, useCallback, useState } from 'react'
import { IconTrash } from '@tabler/icons-react'
import { enqueueSnackbar } from 'notistack'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogFooter } from '../../atoms/alert-dialog'
import Typography from '../../atoms/Typography'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { Input } from '../../atoms/inputs/Input'
import { api } from '../../../api/root'

interface DeleteSelectedProjectsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectIds: string[]
  /** Required when projectIds.length === 1 to show name-confirm flow */
  singleProjectName?: string
  onDeleted: () => void
}

const DeleteSelectedProjectsDialog: FC<DeleteSelectedProjectsDialogProps> = ({
  open,
  onOpenChange,
  projectIds,
  singleProjectName,
  onDeleted,
}) => {
  const [confirmName, setConfirmName] = useState('')
  const isSingle = projectIds.length === 1
  const count = projectIds.length

  const handleClose = useCallback(() => {
    onOpenChange(false)
    setConfirmName('')
  }, [onOpenChange])

  const { mutate: deleteSingle, isPending: isSinglePending } = api.project.delete.useMutation({
    onSuccess: () => {
      enqueueSnackbar('1 project deleted successfully', { variant: 'success' })
      handleClose()
      onDeleted()
    },
  })

  const { mutate: deleteMany, isPending: isManyPending } = api.project.deleteMany.useMutation({
    onSuccess: ({ deletedCount }) => {
      enqueueSnackbar(`${deletedCount} ${deletedCount === 1 ? 'project' : 'projects'} deleted successfully`, {
        variant: 'success',
      })
      handleClose()
      onDeleted()
    },
  })

  const isPending = isSinglePending || isManyPending

  const handleDelete = useCallback(() => {
    if (isSingle) {
      deleteSingle(projectIds[0])
    } else {
      deleteMany(projectIds)
    }
  }, [isSingle, deleteSingle, deleteMany, projectIds])

  const isDeleteDisabled = isSingle ? isPending || confirmName !== singleProjectName : isPending

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <AlertDialogContent>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFADAD]">
            <IconTrash className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex flex-col justify-center gap-6">
            <Typography variant="2xl-bold" className="text-center text-green-50">
              Delete {isSingle ? 'Project' : 'Projects'}
            </Typography>
            {isSingle ? (
              <>
                <Typography variant="base" className="text-center text-gray-50">
                  You&apos;re about to delete the{' '}
                  <Typography variant="base-semibold" className="inline">
                    {singleProjectName}
                  </Typography>{' '}
                  project. Please type the project name to confirm deletion.
                </Typography>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder="Enter project name"
                  className="text-center"
                />
              </>
            ) : (
              <Typography variant="base" className="text-center text-gray-50">
                You&apos;re about to delete{' '}
                <Typography variant="base-semibold" className="inline">
                  {count} projects
                </Typography>
                . This action cannot be undone. Are you sure you want to proceed?
              </Typography>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={handleClose} className="w-40">
            Cancel
          </AlertDialogCancel>
          <PrimaryButton
            disabled={isDeleteDisabled}
            isLoading={isPending}
            onClick={handleDelete}
            className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
          >
            Delete
          </PrimaryButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteSelectedProjectsDialog
